// RF Admin API client — HttpOnly Cookie Only(P2-4 拍板,2026-09-18)
// token **永不存 sessionStorage / localStorage**,只靠 rf_admin_at / rf_admin_rt
// httponly cookie。后端自动 Set-Cookie,前端用 credentials: "include" 发送。
//
// 防御 XSS 偷 token / 避免前端双份状态不同步。
//
// 2026-09-19 加 refresh-on-401 拦截器:
// access token 15 分钟过期,refresh token 7 天。所有 rf-admin API 调用通过
// rfAdminFetch 包装,401 → POST /api/rf-admin/auth/refresh(用 rf_admin_rt cookie)
// 换新 access → 重试原请求。refresh 失败才抛错(此时用户已登录态丢失,
// 上层路由会跳 /rf-admin/login)。
// refresh 单飞:并发 401 只发一次 refresh,其他等结果。login/logout/refresh 自身
// 走普通 fetch,不进包装(避免循环)。

import { ApiError } from "../../api/client";

export interface RfAdminUser {
  id: number;
  email: string;
  role: "super" | "sub";
  status: "active" | "disabled";
  last_login_at: string | null;
  totp_enabled: boolean;
  created_at: string;
}

interface LoginResponse {
  success: boolean;
  data: {
    admin: RfAdminUser;
    // 注意:token 不在这里返回(P2-4),只在 httponly cookie 里
  };
  error: unknown;
}

interface MeResponse {
  success: boolean;
  data: { admin: RfAdminUser };
  error: unknown;
}

// ────────────────────── refresh-on-401 单飞包装 ──────────────────────

let refreshInFlight: Promise<boolean> | null = null;

async function _tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const resp = await fetch("/api/rf-admin/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      return resp.ok;
    } catch {
      return false;
    } finally {
      // 等 microtask 跑完再清,让并发的 awaiter 拿到同一份 result
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

/**
 * RF Admin fetch 包装:401 → 自动 refresh → 重试一次。
 * login / logout / refresh 自身不要走这个(否则死循环或语义错)。
 */
export async function rfAdminFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const opts: RequestInit = { ...options, credentials: "include" };
  let resp = await fetch(url, opts);
  if (resp.status !== 401) return resp;

  // 401:尝试 refresh(单飞)
  const refreshed = await _tryRefresh();
  if (!refreshed) return resp;

  // refresh 成功:重试原请求一次
  resp = await fetch(url, opts);
  return resp;
}

// ────────────────────── API ──────────────────────

/** 登录。成功后由后端 set-cookie,前端不需要手动保存 token。 */
export async function rfAdminLogin(email: string, password: string): Promise<RfAdminUser> {
  const resp = await fetch("/api/rf-admin/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  const body = (await resp.json()) as LoginResponse;
  if (!body.success) {
    const err = body.error as { code: string; message: string };
    throw new ApiError(err.code, resp.status, err.message);
  }
  return body.data.admin;
}

/** 登出。后端清 cookie,前端不需要手动清 token。 */
export async function rfAdminLogout(): Promise<void> {
  await fetch("/api/rf-admin/auth/logout", {
    method: "POST",
    credentials: "include",
  });
}

/** 拉当前 admin。未登录 → 返回 null(后端 401 → catch 后返 null)。 */
export async function rfAdminMe(): Promise<RfAdminUser | null> {
  const resp = await rfAdminFetch("/api/rf-admin/me");
  if (!resp.ok) return null;
  const body = (await resp.json()) as MeResponse;
  return body.success ? body.data.admin : null;
}

/** 改密。RFC §M4。已登录态走 cookie 鉴权。 */
export async function rfAdminChangePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  const resp = await rfAdminFetch("/api/rf-admin/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
  const body = (await resp.json()) as { success: boolean; error: { code: string; message: string } };
  if (!body.success) {
    throw new ApiError(body.error.code, resp.status, body.error.message);
  }
}